import { Send, BellRing, MessageSquareReply, Trophy, ShieldCheck } from "lucide-react";

const WEEK_MS = 7 * 24 * 3600 * 1000;

// Next mail number (2/3) whose waiting time has passed, else 0.
export const followUpDue = (crm) => {
  if (!crm || crm.status !== "active" || !crm.next_send_at) return 0;
  const step = crm.step || 0;
  if (step < 1 || step > 2) return 0;
  return new Date(crm.next_send_at) <= new Date() ? step + 1 : 0;
};

const BLOCKED = ["unsubscribed", "replied", "customer", "bounced", "paused"];

export const SENT_FILTERS = {
  unsent: (c) => (c.emails || []).length > 0 && !(c.crm || {}).last_sent_at
    && !((c.crm || {}).step > 0) && !BLOCKED.includes((c.crm || {}).status),
  week: (c) => {
    const at = (c.crm || {}).last_sent_at;
    return !!at && Date.now() - new Date(at).getTime() <= WEEK_MS;
  },
  due: (c) => followUpDue(c.crm) > 0,
  customers: (c) => (c.crm || {}).status === "customer",
  failed: (c) => !!c.scrape_failed,
  perm_asked: (c) => (c.crm || {}).permission === "asked",
  perm_granted: (c) => (c.crm || {}).permission === "granted",
  due2: (c) => followUpDue(c.crm) === 2,
  due3: (c) => followUpDue(c.crm) === 3,
  soon: (c) => {
    const crm = c.crm || {};
    if (crm.status !== "active" || !crm.next_send_at || !(crm.step >= 1 && crm.step <= 2)) return false;
    const t = new Date(crm.next_send_at).getTime() - Date.now();
    return t > 0 && t <= 3 * 24 * 3600 * 1000;
  },
};

const fmt = (d) => `${d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "2-digit" })} ${d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;

const PermissionPart = ({ crm, onPermission, testid }) => {
  const p = crm?.permission;
  if (p === "granted") return <span data-testid={`perm-granted-${testid}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2 py-0.5 font-semibold"><ShieldCheck className="w-3 h-3" /> Toestemming</span>;
  if (p === "denied") return <span data-testid={`perm-denied-${testid}`} className="rounded-full bg-slate-200 text-slate-600 px-2 py-0.5 font-semibold">Geen toestemming</span>;
  if (p !== "asked") return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span data-testid={`perm-asked-${testid}`} className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">Toestemming gevraagd</span>
      {onPermission && (
        <>
          <button data-testid={`perm-yes-${testid}`} onClick={() => onPermission("granted")} title="Antwoordde ja: Mail 1–3 mogen nu"
            className="rounded-full border border-emerald-600 text-emerald-700 px-2 py-0.5 hover:bg-emerald-50 transition-colors">Toestemming ✓</button>
          <button data-testid={`perm-no-${testid}`} onClick={() => onPermission("denied")} title="Antwoordde nee: geen mails meer"
            className="rounded-full border border-slate-300 text-slate-500 px-2 py-0.5 hover:bg-slate-100 transition-colors">Nee</button>
        </>
      )}
    </span>
  );
};

export const SentCell = ({ crm, onReply, onCustomer, onPermission, testid }) => {
  const at = crm?.last_sent_at;
  const due = followUpDue(crm);
  const d = at ? new Date(at) : null;
  const when = d ? fmt(d) : "";
  const isCustomer = crm?.status === "customer";
  const replied = crm?.status === "replied" || isCustomer;
  const canReply = !replied && crm?.permission !== "asked" && (crm?.step > 0 || !!at) && !BLOCKED.includes(crm?.status);
  const canCustomer = !isCustomer && (canReply || crm?.status === "replied");
  if (!d && !due && !replied && !canReply && !crm?.permission) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-col items-start gap-1 text-xs">
      <PermissionPart crm={crm} onPermission={onPermission} testid={testid} />
      {d && (
        <span className="inline-flex items-center gap-1.5" title={d.toLocaleString("nl-NL")}>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-semibold">
            <Send className="w-3 h-3" strokeWidth={2} /> {crm.step ? `Mail ${crm.step}` : crm.permission ? "Toestemming" : "Mail 1"}
          </span>
          <span className="text-slate-500">{when}</span>
        </span>
      )}
      {due > 0 && !replied && (
        <span data-testid="followup-due-badge" className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-[#FF4500] px-2 py-0.5 font-semibold animate-pulse">
          <BellRing className="w-3 h-3" strokeWidth={2} /> Mail {due} klaar
        </span>
      )}
      {replied && (
        <span data-testid={`replied-label-${testid}`} className={`inline-flex items-center gap-1 rounded-full text-white px-2 py-0.5 font-semibold ${isCustomer ? "bg-amber-500" : "bg-green-600"}`}>
          {isCustomer ? <Trophy className="w-3 h-3" strokeWidth={2} /> : <MessageSquareReply className="w-3 h-3" strokeWidth={2} />}
          {isCustomer ? "Klant" : "Gereageerd"}
          {(isCustomer ? crm.customer_at || crm.replied_at : crm.replied_at) && (
            <span className="font-normal opacity-90">· {fmt(new Date(isCustomer ? crm.customer_at || crm.replied_at : crm.replied_at)).split(" ")[0]}</span>
          )}
        </span>
      )}
      <div className="flex flex-wrap gap-1">
        {canReply && onReply && (
          <button
            data-testid={`reply-btn-${testid}`}
            onClick={onReply}
            title="Deze vereniging heeft gereageerd: stopt de vervolgmails en telt mee als reactie"
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-slate-600 hover:border-green-600 hover:text-green-700 hover:bg-green-50 transition-colors"
          >
            <MessageSquareReply className="w-3 h-3" strokeWidth={2} /> Reageerde
          </button>
        )}
        {canCustomer && onCustomer && (
          <button
            data-testid={`customer-btn-${testid}`}
            onClick={onCustomer}
            title="Klant geworden: stopt de vervolgmails en telt mee als conversie in het weekoverzicht"
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-slate-600 hover:border-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Trophy className="w-3 h-3" strokeWidth={2} /> Klant geworden
          </button>
        )}
      </div>
    </div>
  );
};
